// Developer: Ankit Gupta
/** My timesheet (backend app/api/v1/timesheet.py). A confirmed week can be saved to OTL as PENDING rows; it is submitted in OTL. */
import { api } from '@/api/client';

export interface Line {
  id: number | null;
  project_id: number | null;
  task_id: number | null;
  note: string;
  /** Five numbers, Sunday to Thursday. */
  hours: number[];
  source?: 'ORION' | 'PERSON';
  thread_ids: number[];
}

export type ProblemCode =
  | 'PERIOD_CLOSED' | 'WEEK_IN_OTL' | 'NO_LINES' | 'LINE_INVALID' | 'LINE_EMPTY' | 'LINE_DAY' | 'NOTE_MISSING' | 'NOTE_LONG' | 'DAY_HOURS';

export interface Problem {
  code: ProblemCode;
  line?: number;
  lines?: number;
  day?: number;
  total?: number;
}

export interface Week {
  week_start: string;
  dates: string[];
  day_hours: number;
  otl_url: string;
  /** ONE when the person's email matches one person in OTL. */
  match: 'ONE' | 'NONE' | 'MANY';
  person?: { name: string; person_number: number };
  period?: { name: string; open: boolean } | null;
  projects?: { id: number; name: string }[];
  tasks?: Record<string, { task_id: number; task_name: string }[]>;
  in_otl?: { status: string; lines: number; hours: number }[];
  status?: 'DRAFT' | 'READY' | 'SAVED' | null;
  lines?: Line[];
  day_reasons?: Record<string, string>;
  generated_at?: string | null;
  ready_at?: string | null;
  saved_at?: string | null;
  /** True for a ready week once the writing account may build OTL's per-day rows. */
  save_open?: boolean;
  threads?: { id: number; title: string }[];
  problems?: Problem[];
}

export interface WeekIn {
  lines: Line[];
  day_reasons: Record<string, string>;
}

export const timesheetApi = {
  access: () => api.get<{ enabled: boolean }>('/timesheet/access'),
  week: (week: string) => api.get<Week>(`/timesheet/weeks/${week}`),
  generate: (week: string) => api.post<Week>(`/timesheet/weeks/${week}/generate`),
  save: (week: string, body: WeekIn) => api.put<Week>(`/timesheet/weeks/${week}`, body),
  ready: (week: string) => api.post<Week>(`/timesheet/weeks/${week}/ready`, { confirmed: true }),
  reopen: (week: string) => api.post<Week>(`/timesheet/weeks/${week}/reopen`),
  saveToOtl: (week: string) => api.post<Week>(`/timesheet/weeks/${week}/save-to-otl`, { confirmed: true }),
};
